import type { ChoiceValue, IntervieweeDisplacement } from "../domain/contracts";

const DISPLACEMENT_REASON_LABELS: Record<string, string> = {
  discrimination_in_education: "Discrimination in education",
  discrimination_in_health: "Discrimination in health",
  discrimination_in_movement: "Discrimination in movement",
  harassment_by_authorities: "Harassment by authorities",
  land_confiscated: "Land confiscated",
  fear_for_life: "Fear for life",
};

const OTHER_REASON_CODES = new Set([
  "other",
  "other_reason",
  "other_reason_for_leaving_village",
]);

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function humanizeCode(code: string): string {
  return code
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function mapReasonLabel(reason: ChoiceValue, otherReason: string | null): string | null {
  const code = nonEmpty(reason.code)?.toLowerCase() ?? null;
  const label = nonEmpty(reason.label);

  if (code && OTHER_REASON_CODES.has(code)) {
    return otherReason ?? label ?? "Other";
  }

  if (label) {
    return label;
  }

  if (code && DISPLACEMENT_REASON_LABELS[code]) {
    return DISPLACEMENT_REASON_LABELS[code];
  }

  if (code) {
    return humanizeCode(code);
  }

  return null;
}

export function buildDisplacementReasonSummary(displacement: IntervieweeDisplacement | null | undefined): string {
  if (!displacement) {
    return "-";
  }

  const otherReason = nonEmpty(displacement.otherReason);
  const mappedReasons = (displacement.reasons ?? [])
    .map((reason) => mapReasonLabel(reason, otherReason))
    .filter((reason): reason is string => Boolean(reason));
  const uniqueReasons = Array.from(new Set(mappedReasons));

  // Surface free-text reason when present, even if enum selections are incomplete.
  if (otherReason && !uniqueReasons.includes(otherReason)) {
    uniqueReasons.push(otherReason);
  }

  return uniqueReasons.length > 0 ? uniqueReasons.join(", ") : "-";
}
